const SWAP_TRIGGER_SELECTOR = '[data-bundle-swap]';
const BUNDLE_QUANTITY = 1;

// `key~quantity` → [key, quantity]; [] when malformed.
const parseSpec = (spec) => {
  const separator = spec.lastIndexOf('~');
  if (separator === -1) return [];

  const quantity = Number.parseInt(spec.slice(separator + 1), 10);
  if (Number.isNaN(quantity)) return [];

  return [spec.slice(0, separator), quantity];
};

class CartBundleSwap extends HTMLElement {
  connectedCallback() {
    this.handleClick = (event) => this.swap(event);
    this.addEventListener('click', this.handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
  }

  // Every input is read off the live button at click time and nothing is cached:
  // a line item key changes whenever the line's properties or discount
  // applications change, so a key held across a repaint is already stale.
  // Idiomorph rewrites these attributes in place, so the click-time read is
  // always the freshly rendered value.
  swap(event) {
    const button = event.target.closest(SWAP_TRIGGER_SELECTOR);
    if (!button || button.disabled) return;

    event.preventDefault();

    const { variantId, componentKeys, bundleLine } = button.dataset;
    if (!variantId) return;

    // `key~quantity` pairs: each component gives up one unit, so extra units
    // survive the swap. Keyed by line item key, never variant id — a variant
    // key updates only the first matching line. The add goes last so the two
    // key spaces stay legible in the payload.
    const updates = {};
    for (const spec of (componentKeys ?? '').split(',').filter(Boolean)) {
      const [key, quantity] = parseSpec(spec);
      if (key) updates[key] = quantity;
    }
    if (!Object.keys(updates).length) return;

    const host = this.closest('cart-drawer');
    if (!host) return;

    // An existing bundle line is raised by one; a variant-keyed quantity would
    // set it, so a repeat swap would take a set and add nothing.
    const [bundleKey, bundleQuantity] = parseSpec(bundleLine ?? '');
    if (bundleKey) updates[bundleKey] = bundleQuantity;
    else updates[variantId] = BUNDLE_QUANTITY;

    // One request, never remove-then-add: an interrupted two-call sequence
    // leaves the cart visibly broken. No lineKey is passed — the button resolves
    // to this component's own error slot, and the repaint comes from the
    // server's actual cart state.
    return host.mutate((sections) => CartAPI.update({ updates }, sections), {
      control: button,
      announce: host.announcement('announceBundleSwapped', {
        '%product%': button.dataset.productTitle ?? '',
      }),
    });
  }
}

if (!customElements.get('cart-bundle-swap')) {
  customElements.define('cart-bundle-swap', CartBundleSwap);
}
